import { Upload } from "lucide-react";
import { useRef, useState } from "react";
import { Modal } from "./Modal";
import { clients as clientsApi } from "../lib/api";

export function CsvImportDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ created: number; errors: Array<{ row: number; message: string }> } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function reset() {
    setFile(null); setBusy(false); setResult(null); setError(null);
  }

  async function submit() {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const res = await clientsApi.importCsv(file);
      setResult(res);
      onDone();
    } catch (e: any) {
      setError(e?.message || "Import failed");
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="Import clients from CSV"
      footer={
        result ? (
          <button className="primary-button" onClick={() => { reset(); onClose(); }}>Done</button>
        ) : (
          <>
            <button className="ghost-button" onClick={() => { reset(); onClose(); }}>Cancel</button>
            <button className="primary-button" disabled={!file || busy} onClick={submit}>
              {busy ? "Uploading…" : "Import"}
            </button>
          </>
        )
      }
    >
      {result ? (
        <div>
          <p>{result.created} client(s) imported.</p>
          {result.errors.length > 0 && (
            <>
              <p className="error-text">{result.errors.length} row(s) failed:</p>
              <ul className="error-list">
                {result.errors.map((e, i) => (
                  <li key={i}>Row {e.row}: {e.message}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      ) : (
        <>
          <div
            className="dropzone"
            role="button"
            tabIndex={0}
            aria-label="Click or drop a CSV file to import"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
          >
            <Upload size={24} aria-hidden="true" />
            <strong>{file ? file.name : "Drop a CSV here or click to choose"}</strong>
            <span className="muted small">
              Columns: legal_name, email, phone, address_line1, address_city, address_state, address_zip
            </span>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          {error && <p className="error-text">{error}</p>}
        </>
      )}
    </Modal>
  );
}
